import Template from "../app/Template";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useParams, useSearchParams } from "react-router-dom";
import Card from "../layout/Card";
import { StarIcon } from "@heroicons/react/24/solid";
import CardWithImageTopAndFooter from "../layout/CardWithImageTopAndFooter";
import SectionHeader from "../layout/SectionHeader";
import { useEffect, useState } from "react";
import { getCategoryOptions } from "../../slices/category";
import { getCollectionBySlug } from "../../slices/content";
import { openLoginPrompt } from "../../slices/auth";
import ExploreTopicsAndCollections from "../layout/ExploreTopicsAndCollections";
import Quote from "../landing/Quote";
import SupportUs from "../landing/SupportUs";
import { Author, Tag, Theme } from "../../types/content";
import OtherContent from "../layout/OtherContent";
import ConsumptionButton from "../layout/ConsumptionButton";
import Spinner from "../layout/Spinner";
import Button from "../layout/Button";
import { LockClosedIcon } from "@heroicons/react/24/solid";


export default function CollectionBySlug() {
  const dispatch = useAppDispatch();

  // Get URL params
  const { collection_slug: collectionSlug } = useParams()

  // Get query params
  const [searchParams, setSearchParams] = useSearchParams();
  const querySearch = searchParams.get("query");
  const queryAudience = searchParams.get("audience");
  const queryConsumption = searchParams.get("consumption");
  const queryType = searchParams.get("type") || "Post";
  const queryPageNumber = searchParams.get("page") === null
    ? 1
    : parseInt(searchParams.get("page") as string);

  // Get app level state from redux store
  const { user, loading: authLoading } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (authLoading) return;
    dispatch(getCollectionBySlug({
      slug: collectionSlug,
      pageNumber: queryPageNumber,
      query: querySearch,
      contentType: queryType,
      consumption: queryConsumption,
      audience: queryAudience,
      profileId: user === null ? null : user?.profile.id
    }));
  }, [
    authLoading,
    collectionSlug,
    queryPageNumber,
    querySearch,
    queryType,
    queryConsumption,
    queryAudience,
  ]);

  useEffect(() => {
    dispatch(getCategoryOptions())
  }, []);

  const {
    collection: { data: collectionData, loading: collectionLoading, waiting: collectionWaiting, pagination: collectionPagination },
  } = useAppSelector((state) => state.content);
  const {
    options: { data: optionsData, loading: optionsLoading }
  } = useAppSelector((state) => state.category);

  // Component level state
  const [searchState, setSearchState] = useState("");
  const [activeHeroPostIndex, setActiveHeroPostIndex] = useState(0);
  const [activeTheme, setActiveTheme] = useState<string>("");

  useEffect(() => {
    if (
      collectionData !== null &&
      !collectionLoading &&
      // collectionData.themes.length > 0 &&
      activeTheme === ""
    ) {
      if (collectionData.theme_1 !== null) {
        setActiveTheme(collectionData.theme_1.id);
      } else if (collectionData.theme_2 !== null) {
        setActiveTheme(collectionData.theme_2.id);
      } else if (collectionData.theme_3 !== null) {
        setActiveTheme(collectionData.theme_3.id);
      } else {
        setActiveTheme(""); // Fallback if no themes are available
      }
    }
  }, [collectionData, collectionLoading, activeTheme]);

  // Get themes
  const themes: Theme[] = [];
  if (collectionData !== null && collectionData.theme_1 !== undefined && collectionData.theme_1 !== null) {
    themes.push(collectionData.theme_1);
  }
  if (collectionData !== null && collectionData.theme_2 !== undefined && collectionData.theme_2 !== null) {
    themes.push(collectionData.theme_2);
  }
  if (collectionData !== null && collectionData.theme_3 !== undefined && collectionData.theme_3 !== null) {
    themes.push(collectionData.theme_3);
  }

  const heroTheme = themes.find((theme: Theme) => theme.id === activeTheme) || null;
  const heroPostIds = heroTheme === null ? [] : heroTheme.hero_posts.map((post: any) => post.id);
  const finalHeroPostIds = heroPostIds === undefined ? [] : heroPostIds;
  const heroPostsToShow = finalHeroPostIds.length === 0 ? collectionData?.hero_posts : collectionData?.hero_posts.filter((post: any) => finalHeroPostIds.includes(post.id));
  const finalHeroPostsToShow = heroPostsToShow === undefined ? [] : heroPostsToShow;

  // Undefined while the rotation index still points past the end of a freshly
  // filtered list -- switching theme replaces the array before the index resets.
  const heroPost: any = finalHeroPostsToShow[activeHeroPostIndex];

  useEffect(() => {
    // Every 5 seconds, update the active hero post index and then reset to 0 when it's at the end of the array
    const interval = setInterval(() => {
      setActiveHeroPostIndex((prevIndex) => (prevIndex + 1) % finalHeroPostsToShow.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [finalHeroPostsToShow.length]);

  // Constant variables
  const interestFilters = optionsData.interest === undefined ? [] : optionsData.interest.map((x: any) => x.name);
  const dropdownTypes: Array<{ label: string; value: string }> = [{ label: "Posts", value: "Post" }, { label: "People", value: "People" }, { label: "Events", value: "Event" }, { label: "Tools", value: "Tool" }, { label: "Media", value: "Media" }];
  const dropdownAudience: Array<{ label: string; value: string }> = optionsData.audience === undefined ? [] : optionsData.audience.map((x: any) => ({ label: x.name, value: x.name }));
  const dropdownConsumption: Array<{ label: string; value: string }> = [{ label: "Read", value: "Read" }, { label: "Watch", value: "Watch" }, { label: "Listen", value: "Listen" }];

  // Members-only collection the visitor cannot see. The API has already
  // withheld the themes and the curated items -- this only decides what to show
  // in their place, so the page is an invitation rather than an empty shell.
  const locked = collectionData !== null && collectionData.locked === true;

  return (
    <Template navbarMode="solid" footerBgColour="off-white">

      <div className="grid grid-col-1 gap-y-20 mb-20">
        {/* Hero */}
        <div>
          <div
            className="mt-40 flex items-center justify-center w-9/10 lg:w-4/5 mx-auto bg-off-white relative"
          >
            <Card colour="yellow" className="w-full grid grid-cols-1 gap-y-4">
              <span className="bg-white px-6 py-2 rounded-full text-black w-fit shadow-md text-base font-bold">Collection</span>
              <h1 className="text-dark-grey text-3xl md:text-5xl lg:text-6xl font-bold">{collectionData === null ? "Loading..." : collectionData.name}</h1>
              {collectionData !== null && !collectionLoading && (
                <p className="text-dark-grey text-lg">{collectionData.description}</p>
              )}
            </Card>

            {/* Bubbles */}
            <img
              src="/img/graphics/bubbles/bubble-top-orange.svg"
              alt="Bubble"
              className="absolute w-24 object-cover top-1/2 right-4 z-0"
            />
            <img
              src="/img/graphics/bubbles/bubble-right-blue.svg"
              alt="Bubble"
              className="absolute w-48 object-cover -bottom-10 right-28 z-0"
            />
          </div>
        </div>

        {/* Members-only gate */}
        {locked && (
          <div className="w-9/10 lg:w-4/5 mx-auto">
            <Card colour="dark-beige" className="grid grid-cols-1 gap-y-4 text-center justify-items-center py-12">
              <span className="flex items-center gap-2 bg-dark-blue text-white px-4 py-1 rounded-full text-sm font-bold">
                <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
                Members only
              </span>
              <h2 className="text-dark-grey text-2xl md:text-3xl font-bold">
                This collection is for members
              </h2>
              <p className="text-dark-grey text-base max-w-xl">
                Sign in to see everything {collectionData?.name} brings together. It is free to
                join, and your saved content comes with you.
              </p>
              <Button
                bgColour="dark-blue"
                borderColour="dark-blue"
                textColour="white"
                width="fit"
                onClick={() => dispatch(openLoginPrompt())}
              >
                Sign in
              </Button>
            </Card>
          </div>
        )}

        {/* Hero content */}
        {!locked && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-x-4 w-9/10 lg:w-4/5 px-6 md:px-0 mx-auto w-full md:w-full h-fit md:h-[600px] overflow-auto scrollbar-invisible">

          {/* Theme column */}
          <div className="col-span-2 hidden md:block grid grid-cols-1 overflow-y-auto scrollbar-invisible">
            <div className={`grid-rows-${themes.length} space-y-4`}>
              {collectionData !== null && !collectionLoading && themes.map((theme: Theme, idx: number) => (
                <Card
                  key={theme.id}
                  colour={activeTheme === theme.id ? "dark-blue" : "transparent"}
                  className="w-full grid grid-cols-1 gap-y-4 text-left cursor-pointer"
                  borderColour="dark-blue"
                  borderWeight="2"
                  paddingX="px-4 lg:px-6"
                  paddingY="px-4 lg:py-6"
                  rounded="2xl"
                  onClick={() => {
                    setActiveHeroPostIndex(0)
                    setActiveTheme(theme.id)
                  }}
                >
                  <h1 className={`text-${activeTheme === theme.id ? "white" : "dark-blue"} text-md lg:text-lg font-bold`}>{theme.name}</h1>
                  <p className={`text-${activeTheme === theme.id ? "white" : "dark-blue"} text-sm`}>{theme.summary}</p>
                </Card>
              ))}
            </div>
          </div>
          <div className="block md:hidden flex gap-x-4 mb-4 overflow-x-auto w-full scrollbar-invisible">
            {collectionData !== null && !collectionLoading && themes.map((theme: Theme, idx: number) => (
              <Card
                key={idx}
                colour={activeTheme === theme.id ? "dark-blue" : "transparent"}
                className="w-80 shrink-0 grid grid-cols-1 gap-y-4 text-left cursor-pointer"
                borderColour="dark-blue"
                borderWeight="2"
                paddingX="px-4"
                paddingY="py-4"
                rounded="2xl"
                onClick={() => {
                  setActiveHeroPostIndex(0)
                  setActiveTheme(theme.id)
                }}
              >
                <h1 className={`text-${activeTheme === theme.id ? "white" : "dark-blue"} text-md lg:text-lg font-bold`}>{theme.name}</h1>
                <p className={`text-${activeTheme === theme.id ? "white" : "dark-blue"} text-sm`}>{theme.summary}</p>
              </Card>
            ))}
          </div>

          {/* Hero posts column */}
          <div className="col-span-3 grid grid-cols-1">
            {collectionLoading ? <Spinner /> : finalHeroPostsToShow.length === 0 ? <p>No hero content</p> : (
              <>
                {heroPost !== undefined && (
                  <CardWithImageTopAndFooter
                    key={`content-hero-`}
                    size="hero"
                    objectId={heroPost.id}
                    objectType="Post"
                    href={heroPost.url}
                    locked={heroPost.locked}
                    image={heroPost.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : heroPost.images[0]}
                    bgColour="dark-beige"
                    dataTitle={heroPost.title}
                    dataAuthor={heroPost.authors.map((author: Author) => author.name).join(", ")}
                    dataTags={heroPost.primary_tags.map((tag: Tag) => tag.name).join(", ")}
                    middleContent={
                      <>
                        {/* Title */}
                        <h1 className="text-xl font-bold">{heroPost.title}</h1>

                        {/* Collection */}
                        {heroPost.collections.length > 0 && (
                          <p className="text-sm font-medium">{heroPost.collections[0].name}</p>
                        )}

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {heroPost.primary_tags.map((tag: Tag, index: number) => {
                            return (
                              <span key={`tag-${index}`} className="flex items-center bg-transparent border border-dark-grey text-dark-grey px-1.5 py-0.2 rounded-full text-xs font-bold">{tag.name}</span>
                            );
                          })}
                        </div>

                        {/* Synopsis */}
                        <p className="text-sm">{heroPost.synopsis}</p>
                      </>
                    }
                    footerContent={(openContent, opensInNewTab) => (
                      <>
                        {/* Consumption — the footer's way through to the content */}
                        {heroPost.consumption.length > 0 && (
                          <ConsumptionButton
                            consumption={heroPost.consumption[0].name}
                            title={heroPost.title}
                            onClick={openContent}
                            opensInNewTab={opensInNewTab}
                            locked={heroPost.locked}
                          />
                        )}

                        {/* Position */}
                        <div className="flex flex-1 items-center justify-center gap-1">
                          {[...Array(finalHeroPostsToShow.length)].map((_, index) => {
                            return (
                              <span
                                key={`position-${index}`}
                                className={`z-50 h-4 p-2 bg-${activeHeroPostIndex === index ? "white" : "black/20"} rounded-full`}
                              />
                            )
                          })}
                        </div>

                        {/* Rating */}
                        <div className={`group flex items-center gap-1`}>
                          <StarIcon className={`shrink-0 h-5 w-5 text-${user !== null ? "yellow" : "secondary"}`} />
                          {user !== null && (
                            <>
                              <span className="text-sm font-bold">{heroPost.rating}</span>
                              <span className="text-sm font-bold">({heroPost.reviews})</span>
                            </>
                          )}
                        </div>

                      </>
                    )}
                  />
                )}
              </>
            )}

          </div>

        </div>
        )}

        {/* Quote */}
        <div className="mb-10">
          <Quote text={collectionLoading ? "" : collectionData?.quote.text} subtext={collectionLoading ? "" : `${collectionData?.quote.author}, ${collectionData?.quote.source}`} />
        </div>

        {/* Other content */}
        {!locked && (
          <OtherContent showPagination={true} topic={undefined} collection={collectionSlug} />
        )}

        {/* You may also like */}
        <SectionHeader
          text="You may also like"
        />

        {/* Topics and collections */}
        <ExploreTopicsAndCollections />

        {/* Support us */}
        <SupportUs />

      </div>

    </Template >
  );
}
